import { activeSegments, pastSegments } from "pages/RequestIndex/hooks";
import { useModal } from "swing-components";

import { FilterChip } from "~components/Organisms/IndexFilters/FilterChip";
import { translate, useIsDesktop } from "~utils";

import { SchoolsModal } from "./SchoolsModal";
import { initialFilterValues, ThreadedData } from "./types";

type FilterBarProps = ThreadedData;

/**
 * Filter bar for the v2 request index: a schools pill with its own modal,
 * followed by the status quick-toggle chips, with a "Clear filter" text action
 * pinned to the right.
 *
 * There is no combined filter modal in v2 - dates come from the reel above the
 * index, schools from the pill, and statuses from the chips.
 */
export function FilterBar(props: FilterBarProps) {
  const { page, ls, setLs } = props;

  const isDesktop = useIsDesktop();

  const modalOptions = {
    cssClass: isDesktop ? undefined : "action-sheet-modal",
    breakpoints: isDesktop ? undefined : [0, 1],
    initialBreakpoint: isDesktop ? undefined : 1,
  };

  const { openModal: openSchoolsModal, closeModal: closeSchoolsModal } = useModal({
    component: <SchoolsModal ls={ls} setLs={setLs} onDismiss={() => closeSchoolsModal()} />,
    modalOptions,
  });

  const selectedSchools = ls.schools.length;

  return (
    <div
      className="mw-800"
      css={{
        width: "100%",
        marginTop: 16,
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "0 16px",
        "@media (min-width: 900px)": { padding: 0 },
      }}
    >
      {/*
       * The chips wrap onto another line rather than running off the edge.
       * `minWidth: 0` is what lets them: without it this track cannot shrink
       * below one whole chip and a narrow screen overflows instead.
       */}
      <div css={{ display: "flex", flexWrap: "wrap", gap: 10, flex: 1, minWidth: 0 }}>
        <FilterChip selected={selectedSchools > 0} onClick={() => openSchoolsModal()}>
          {translate("LABEL_SCHOOLS")}
          {selectedSchools > 0 && ` (${selectedSchools})`}
        </FilterChip>
        {(page === "active" ? activeSegments : pastSegments).map((item) => {
          return (
            <FilterChip
              key={item}
              selected={ls?.statuses?.includes(item) || false}
              showDismiss
              onClick={() => {
                setLs((old) => {
                  const statuses = old.statuses ?? [];
                  if (statuses.includes(item)) {
                    return { ...old, statuses: statuses.filter((i) => i !== item) };
                  } else {
                    return { ...old, statuses: [...statuses, item] };
                  }
                });
              }}
            >
              <span css={{ textTransform: "capitalize" }}>{item}</span>
            </FilterChip>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => setLs(initialFilterValues)}
        css={{
          flexShrink: 0,
          alignSelf: "center",
          background: "none",
          border: "none",
          padding: 0,
          color: "var(--swing-blue500)",
          cursor: "pointer",
          fontSize: 14,
          whiteSpace: "nowrap",
        }}
      >
        {translate("FILTERS_CLEAR_LABEL")}
      </button>
    </div>
  );
}
